/// Opt-in prompt after sign-in when the guest high score beats the account.

import { create } from 'zustand';

import * as userRepository from '@/user/data/userRepository';
import {
  clearAnonymousHighScore,
  readAnonymousHighScore,
} from '@/user/data/userLocalData';
import type { User } from '@/user/domain/user';

export interface GuestScoreMergeOffer {
  guestScore: number;
  accountScore: number;
}

interface GuestScoreMergeState {
  offer: GuestScoreMergeOffer | null;
  consider: (accountHighScore: number) => void;
  /** Applies guest score to the API; caller updates auth user state. */
  accept: () => Promise<User | null>;
  decline: () => void;
}

export const useGuestScoreMergeStore = create<GuestScoreMergeState>((set, get) => ({
  offer: null,

  consider: (accountHighScore) => {
    const guest = readAnonymousHighScore();
    if (guest <= accountHighScore) {
      if (guest > 0) clearAnonymousHighScore();
      set({ offer: null });
      return;
    }
    set({ offer: { guestScore: guest, accountScore: accountHighScore } });
  },

  accept: async () => {
    const offer = get().offer;
    if (!offer) return null;
    set({ offer: null });
    clearAnonymousHighScore();
    try {
      return await userRepository.updateHighScore(offer.guestScore);
    } catch {
      return null;
    }
  },

  decline: () => {
    set({ offer: null });
    clearAnonymousHighScore();
  },
}));
